// A subscription customer signed in on the website (same mobile number + PIN
// as the customer app). Its own connection, separate from the staff login's
// apiClient, so a staff member and a customer never mix tokens in one browser.
import axios from 'axios';
import { useSyncExternalStore } from 'react';
import { API_BASE_URL } from '../services/api';

const TOKEN_KEY = 'skg.customer.token';
const NAME_KEY = 'skg.customer.name';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

let token = read(TOKEN_KEY);
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function useCustomerToken(): string | null {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => token);
}
export function customerName(): string {
  return read(NAME_KEY) ?? '';
}

function setToken(t: string | null, name?: string) {
  token = t;
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
    if (name) localStorage.setItem(NAME_KEY, name);
    if (!t) localStorage.removeItem(NAME_KEY);
  } catch {
    // private window - signed in for this visit only
  }
  emit();
}

const client = axios.create({ baseURL: API_BASE_URL, timeout: 20000 });
client.interceptors.request.use((config) => {
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
client.interceptors.response.use(undefined, (error) => {
  if (error?.response?.status === 401) setToken(null); // expired (30 days) or PIN changed - sign in again
  return Promise.reject(error);
});

export type Shift = 'Morning' | 'Evening';
export type PayStatus = 'Awaiting payment' | 'Paid - to verify' | 'Verified' | 'Not received' | 'Refunded' | 'Cash on delivery';

export interface Me { id: string; customer_code: string | null; name: string; party_type: string; mobile: string | null; subscription_active: boolean }
export interface TodayQr { token: string; valid_for_date: string; shift: Shift; valid_until: string }
export interface Account {
  billing_mode: 'Prepaid' | 'Postpaid';
  status: 'Ok' | 'LowBalance' | 'WalletEmpty' | 'Due' | 'Overdue';
  balance_due: string;
  daily_value?: string | null;
  wallet_balance?: string | null;
  days_remaining?: number | null;
  billing_cycle?: 'Weekly' | 'Monthly' | null;
  current_cycle_amount?: string | null;
  billed_outstanding?: string | null;
  overdue_amount?: string | null;
  next_due_date?: string | null;
  days_overdue?: number | null;
  last_payment_date?: string | null;
  last_payment_amount?: string | null;
}
export interface MilkRow { id: string; sale_date: string; shift?: string | null; litres: string; amount: string; is_subscription: boolean }
export interface PaymentRow { id: string; amount: string; method: string; paid_at: string; notes?: string | null }
export interface DayPart { shift: Shift; litres?: string | null }
export interface DayChange { id: string; change_date: string; paused: boolean; summary: string; requested_by_customer: boolean }
export interface DayChanges { plan: DayPart[]; changes: DayChange[] }
export interface DayChangeRequest { kind: 'pause' | 'move' | 'quantity'; start_date: string; end_date?: string; to_shift?: Shift; morning_litres?: number; evening_litres?: number; note?: string }
export interface UpiPayment {
  id: string; ref: string; amount: string; status: PayStatus; utr: string | null; upi_id: string | null;
  created_at: string; verified_at: string | null; note: string | null; upi_link: string | null; payee_name: string | null;
}
export interface Address { customer_name: string; mobile: string; flat: string; building: string; area: string; pincode: string }

// ---- signing up on the website, and the customer's own details ----
export type PlanShift = 'Morning' | 'Evening' | 'Both';
export type BillingMode = 'Prepaid' | 'Postpaid';
export interface PlanOption { id: string; name: string; litres_per_day: string; rate_per_litre: string; shift: PlanShift; morning_litres: string | null; evening_litres: string | null }
export interface SignupInfo {
  open: boolean; plans: PlanOption[]; billing: 'choose' | BillingMode; pincodes: string[]; delivery_area_note: string;
  first_start: string; last_start: string; support_phone: string;
}
export interface SubscriptionChoice { plan_id: string; shift: PlanShift; litres_per_day: number; morning_litres?: number; evening_litres?: number; start_date: string }
export interface ProfileForm {
  name: string; email: string; apartment_name: string; house_number: string; address: string; city: string; state: string; pincode: string;
  latitude?: number; longitude?: number;
}
export interface SignupForm extends ProfileForm { mobile: string; pin: string; billing_mode?: BillingMode; subscription?: SubscriptionChoice }
export interface Profile {
  customer_code: string | null; name: string; mobile: string; email: string | null; apartment_name: string | null; house_number: string | null; address: string | null;
  city: string | null; state: string | null; pincode: string | null; has_location: boolean; billing_mode: BillingMode; subscription_active: boolean;
}
interface Paged<T> { items: T[]; total: number }

const get = <T,>(url: string, params?: Record<string, unknown>) => client.get<T>(url, { params }).then((r) => r.data);

export const customerApi = {
  async login(mobile: string, pin: string): Promise<Me> {
    const { data } = await client.post<{ access_token: string; customer: Me }>('/customer-auth/login', { mobile, pin });
    setToken(data.access_token, data.customer.name);
    return data.customer;
  },
  logout: () => setToken(null),
  me: () => get<Me>('/customer/me'),
  qr: () => get<TodayQr>('/customer/today-qr-token'),
  account: () => get<Account>('/customer/account'),
  milk: () => get<Paged<MilkRow>>('/customer/ledger', { page_size: 60 }).then((d) => d.items),
  payments: () => get<Paged<PaymentRow>>('/customer/payments', { page_size: 60 }).then((d) => d.items),
  dayChanges: () => get<DayChanges>('/customer/day-changes'),
  makeChange: (req: DayChangeRequest) => client.post<DayChange[]>('/customer/day-changes', req).then((r) => r.data),
  undoChange: (id: string) => client.delete<DayChange[]>(`/customer/day-changes/${id}`).then((r) => r.data),
  address: () => get<Address>('/customer/profile-address'),
  upiPayments: () => get<UpiPayment[]>('/customer/upi-payments'),
  startUpi: (amount: number) => client.post<UpiPayment>('/customer/upi-payments', { amount }).then((r) => r.data),
  claimUpi: (id: string, utr: string) => client.post<UpiPayment>(`/customer/upi-payments/${id}/paid`, { token: '-', utr }).then((r) => r.data),
  signupInfo: () => get<SignupInfo>('/public/signup'),
  async signup(form: SignupForm): Promise<Me> {
    const { data } = await client.post<{ access_token: string; customer: Me }>('/public/signup', form);
    setToken(data.access_token, data.customer.name);
    return data.customer;
  },
  profile: () => get<Profile>('/customer/profile'),
  saveProfile: (form: ProfileForm) => client.put<Profile>('/customer/profile', form).then((r) => r.data),
  changePin: (current_pin: string, new_pin: string) => client.post('/customer/pin', { current_pin, new_pin }),
  startSubscription: (subscription: SubscriptionChoice, billing_mode?: BillingMode) =>
    client.post<Profile>('/customer/subscription', { subscription, billing_mode }).then((r) => r.data),
};
